/**
 * Diagnostics utility for logging and debugging API interactions
 */
class Diagnostics {
  constructor() {
    this.enabled = process.env.NODE_ENV === 'development';
    this.logs = [];
    this.maxLogs = 100;
  }

  /**
   * Log API request details
   */
  logApiRequest(url, method, headers) {
    if (!this.enabled) return;
    
    const log = {
      type: 'API_REQUEST',
      timestamp: new Date().toISOString(),
      url,
      method,
      headers: this.sanitizeHeaders(headers)
    };
    
    this.addLog(log);
    console.log('[API Request]', log);
  }

  /**
   * Log API error details
   */
  logApiError(error, url, response) {
    if (!this.enabled) return;
    
    const log = {
      type: 'API_ERROR',
      timestamp: new Date().toISOString(),
      url,
      error: error.message,
      status: response?.status,
      statusText: response?.statusText
    };
    
    this.addLog(log);
    console.error('[API Error]', log);
  }

  /**
   * Log authentication status
   */
  logAuthenticationStatus(user, tokens) {
    if (!this.enabled) return;
    
    const log = {
      type: 'AUTH_STATUS',
      timestamp: new Date().toISOString(),
      user: user ? {
        id: user.id,
        email: user.email,
        role: user.role
      } : null,
      hasTokens: !!tokens
    };
    
    this.addLog(log);
    console.log('[Auth Status]', log);
  }

  /**
   * Log chat message
   */
  logChatMessage(message, sessionId, response) {
    if (!this.enabled) return;
    
    const log = {
      type: 'CHAT_MESSAGE',
      timestamp: new Date().toISOString(),
      sessionId,
      messageLength: message?.length,
      hasResponse: !!response,
      responseLength: response?.response?.length
    };
    
    this.addLog(log);
    console.log('[Chat Message]', log);
  }

  /**
   * Log WebSocket connection status
   */
  logWebSocketStatus(status, details) {
    if (!this.enabled) return;
    
    const log = {
      type: 'WEBSOCKET_STATUS',
      timestamp: new Date().toISOString(),
      status,
      details
    };
    
    this.addLog(log);
    console.log('[WebSocket]', log);
  }

  /**
   * Log performance metrics
   */
  logPerformance(operation, duration, details) {
    if (!this.enabled) return;
    
    const log = {
      type: 'PERFORMANCE',
      timestamp: new Date().toISOString(),
      operation,
      duration,
      details
    };
    
    this.addLog(log);
    console.log('[Performance]', `${operation}: ${duration}ms`, details);
  }

  /**
   * Sanitize headers to remove sensitive information
   */
  sanitizeHeaders(headers) {
    if (!headers) return {};
    
    const sanitized = { ...headers };
    
    // Remove or mask sensitive headers
    if (sanitized.Authorization) {
      sanitized.Authorization = 'Bearer [REDACTED]';
    }
    if (sanitized['x-api-key']) {
      sanitized['x-api-key'] = '[REDACTED]';
    }
    
    return sanitized;
  }

  /**
   * Add log entry with size management
   */
  addLog(log) {
    this.logs.push(log);
    
    // Keep only the most recent logs
    if (this.logs.length > this.maxLogs) {
      this.logs = this.logs.slice(-this.maxLogs);
    }
  }

  /**
   * Get all diagnostic logs
   */
  getLogs() {
    return this.logs;
  }

  /**
   * Clear all diagnostic logs
   */
  clearLogs() {
    this.logs = [];
  }

  /**
   * Export logs as JSON
   */
  exportLogs() {
    const data = {
      exportDate: new Date().toISOString(),
      environment: process.env.NODE_ENV,
      logs: this.logs
    };
    
    return JSON.stringify(data, null, 2);
  }

  /**
   * Download logs as a file
   */
  downloadLogs() {
    const data = this.exportLogs();
    const blob = new Blob([data], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagnostics-${Date.now()}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

// Create singleton instance
export const diagnostics = new Diagnostics();

export default diagnostics;